import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import Layout from './Layout';
import Quiz from './Quiz';
import { saveModuleResult } from '../hooks/useProgress';
import { modulesList } from '../data/modules';
import HomeIcon from '@mui/icons-material/Home';
import BookIcon from '@mui/icons-material/Book';
import QuizIcon from '@mui/icons-material/Quiz';
import CelebrationIcon from '@mui/icons-material/Celebration';
import RefreshIcon from '@mui/icons-material/Refresh';

// Styled components for modern quiz design
const QuizContainer = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  padding: theme.spacing(4),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 8px 32px 0 rgba(102, 126, 234, 0.2)',
  maxWidth: '800px',
  margin: '0 auto',
}));

const BreadcrumbContainer = styled(Box)(({ theme }) => ({
  background: '#1a1a2e',
  backdropFilter: 'blur(10px)',
  padding: theme.spacing(2, 3),
  borderRadius: '12px',
  marginBottom: theme.spacing(3),
  border: '1px solid rgba(102, 126, 234, 0.1)',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
}));

const ResultsContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
}));

const ScoreDisplay = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#e2e8f0',
  borderRadius: '20px',
  padding: theme.spacing(4),
  margin: theme.spacing(3, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="100" height="100" xmlns="http://www.w3.org/2000/svg"%3E%3Cdefs%3E%3Cpattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"%3E%3Ccircle cx="50" cy="50" r="1" fill="white" opacity="0.1"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100" height="100" fill="url(%23grain)"/%3E%3C/svg%3E")',
    opacity: 0.1,
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  margin: theme.spacing(1),
  boxShadow: variant === 'contained' ? '0 4px 16px rgba(102, 126, 234, 0.3)' : 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? '0 8px 25px rgba(102, 126, 234, 0.4)' : '0 2px 8px rgba(102, 126, 234, 0.1)',
  },
}));

export default function QuizPage() {
  const { moduleId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [finishedScore, setFinishedScore] = useState(null);

  const module = modulesList.find(m => m.id === moduleId);

  useEffect(() => {
    setLoading(true);
    fetch(`/quizzes/${moduleId}.json`)
      .then((res) => {
        if (!res.ok) throw new Error('not found');
        return res.json();
      })
      .then((data) => {
        setQuestions(data.questions || []);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [moduleId]);

  const handleFinish = (scorePercent) => {
    setFinishedScore(scorePercent);
    saveModuleResult(moduleId, scorePercent);
  };

  const resetQuiz = () => {
    setFinishedScore(null);
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return { message: 'Excelente! Você domina o conteúdo!', emoji: '🎉' };
    if (score >= 80) return { message: 'Muito bom! Continue assim!', emoji: '👏' };
    if (score >= 70) return { message: 'Bom trabalho! Você está no caminho certo!', emoji: '👍' };
    if (score >= 60) return { message: 'Não está mal, mas pode melhorar!', emoji: '💪' };
    return { message: 'Precisa estudar mais. Não desista!', emoji: '📚' };
  };

  if (loading) {
    return (
      <Layout>
        <BreadcrumbContainer>
          <Skeleton variant="text" width={200} height={30} />
        </BreadcrumbContainer>
        
        <QuizContainer elevation={0}>
          <LoadingContainer>
            <CircularProgress 
              size={60} 
              sx={{ 
                color: '#667eea',
                mb: 3,
              }} 
            />
            <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 1 }}>
              Carregando Quiz...
            </Typography>
            <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
              Preparando as questões para você
            </Typography>
          </LoadingContainer>
        </QuizContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb Navigation */}
      <BreadcrumbContainer>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            component={RouterLink}
            to="/"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              color: '#cbd5e1',
              textDecoration: 'none',
              '&:hover': { color: '#667eea' },
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            Início
          </Link>
          <Link
            component={RouterLink}
            to={`/modulos/${moduleId}`}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              color: '#cbd5e1',
              textDecoration: 'none',
              '&:hover': { color: '#667eea' },
            }}
          >
            <BookIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            {module?.title || moduleId}
          </Link>
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 600,
              color: '#f1f5f9',
            }}
          >
            <QuizIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            Quiz
          </Typography>
        </Breadcrumbs>
      </BreadcrumbContainer>

      <QuizContainer elevation={0}>
        {error ? (
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: '12px',
              mb: 3,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#fca5a5',
              '& .MuiAlert-icon': {
                color: '#f87171',
              },
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: '#f1f5f9' }}>
              Quiz não encontrado
            </Typography>
            <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
              Não foi possível carregar as questões. Verifique se o quiz está disponível.
            </Typography>
          </Alert>
        ) : finishedScore !== null ? (
          <ResultsContainer>
            <Box sx={{ mb: 3 }}>
              <CelebrationIcon sx={{ fontSize: '3rem', color: '#667eea', mb: 2 }} />
            </Box>
            
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 1 }}>
              Quiz Concluído!
            </Typography>
            
            <ScoreDisplay>
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 1, position: 'relative', zIndex: 1 }}>
                {finishedScore}%
              </Typography>
              <Typography variant="h6" sx={{ position: 'relative', zIndex: 1 }}>
                {getScoreMessage(finishedScore).message} {getScoreMessage(finishedScore).emoji}
              </Typography>
            </ScoreDisplay>

            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
              <ActionButton
                onClick={resetQuiz}
                variant="outlined"
                startIcon={<RefreshIcon />}
              >
                Tentar Novamente
              </ActionButton>
              <ActionButton
                component={RouterLink}
                to={`/modulos/${moduleId}`}
                variant="outlined"
                startIcon={<BookIcon />}
              >
                Ver Resumo
              </ActionButton>
              <ActionButton
                component={RouterLink}
                to="/"
                variant="contained"
                startIcon={<HomeIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  },
                }}
              >
                Página Inicial
              </ActionButton>
            </Box>
          </ResultsContainer>
        ) : (
          <>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <QuizIcon sx={{ fontSize: '3rem', color: '#667eea', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 1 }}>
                Quiz: {module?.title || moduleId}
              </Typography>
              <Typography variant="body1" sx={{ color: '#cbd5e1' }}>
                Teste seus conhecimentos com {questions.length} questões
              </Typography>
            </Box>
            <Quiz questions={questions} onFinish={handleFinish} />
          </>
        )}
      </QuizContainer>
    </Layout>
  );
}
